<template>
  <DocLayout
    v-if="!isLoading && forum"
    class="layout--topbar"
    :doc="doc"
    :show-text-controls="false"
  >
    <template #page-title>
      <h1 class="title-section__title">Discussion · {{ forum.title }}</h1>
    </template>
    <template #page-content>
      <div class="book-reading__content-editor">
        <div class="discussion__start-btn-box discussion__show-m">
          <router-link
            v-if="!doc.isCommunityEOC"
            :to="{
              name: doc.routeNames().edit_thread,
            }"
            class="btns discussion__start-btn"
          >
            <span class="iconfont iadd-comment"></span>
            Start discussion
          </router-link>
        </div>
        <div class="filter-box filter-box--hide-m">
          <div class="filter-box__flex-box">
            <div class="filter-box__form-box">
              <form class="filter-box__form">
                <CommunitySearch :forum="forum" class="keywords--half" />
              </form>
            </div>
            <div class="filter-box__btn-box discussion__hide-m">
              <router-link
                v-if="!doc.isCommunityEOC"
                :to="{
                  name: doc.routeNames().edit_thread,
                }"
                class="btns btns--large filter-box__btn btns--add iconfont iadd"
              >
                Create Discussion
              </router-link>
            </div>
          </div>
        </div>
        <ToggleButton
          class="discussion-toggle"
          :value="forum.notify_new_thread"
          @update:value="updateToggle"
        >
          Notify me of new discussions for this book
        </ToggleButton>
        <div class="discussion-list">
          <ThreadListItem
            v-for="thread in forum.threads"
            :key="thread.id"
            :thread="thread"
            :doc="doc"
          />
          <EOCButton
            v-if="doc.isCommunityEOC"
            text="Subscribe to Access Discussions "
            ios-text="Full Subscription Required to Access Discussions "
          />
        </div>
      </div>
      <Footer />
    </template>
  </DocLayout>
</template>

<script setup lang="ts">
import { onBeforeMount, Raw, ref } from 'vue'
import { useRouter } from 'vue-router'

import DocLayout from '@/app/components/doc/Layout.vue'
import Footer from '@/app/components/Footer.vue'
import CommunitySearch from '@/app/components/community/Search.vue'
import ThreadListItem from '@/app/components/community/ThreadListItem.vue'
import EOCButton from '@/app/components/billing/EOCButton.vue'
import ToggleButton from '@/components/ui/ToggleButton.vue'

import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { ForumView } from '@/models/forum'
import { DocView } from '@/models/doc'
import { assertNotNull } from '@/helpers/typing'

const props = defineProps<{
  doc: DocView
}>()

const router = useRouter()

const isLoading = ref(true)
const forum = ref<Raw<ForumView> | null>(null)

onBeforeMount(async () => {
  await loadForum()
})

async function loadForum(): Promise<void> {
  try {
    isLoading.value = true
    const response = await backend.getForum(props.doc.forum_id)
    forum.value = new ForumView(response, props.doc)
    isLoading.value = false
  } catch (error: any) {
    if (error.request && error.request.status === 404) {
      // Redirect to the non-existing page
      router.push({ name: 'not_found' })
    } else {
      Sentry.captureException(error)
      throw error
    }
  }
}

async function updateToggle(value: boolean): Promise<void> {
  await assertNotNull(forum.value).toggleNotifyNewThread(value)
}
</script>
